import { useQuery } from '@apollo/react-hooks';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Card from 'components/Card';
import GetUserCreditsAdmin from 'pages/app/Users/gql/queries/get-user-credits-admin';
import GetUserOpenCreditsTotalAdmin from 'pages/app/Users/gql/queries/get-user-open-credits-total-admin';
import { AppContext } from 'shared/AppContext';
import GetUserCredits from '../../gql/get-user-credits';
import GetUserOpenCreditsTotal from '../../gql/get-user-open-credits-total';

// Styled Components - Matching Design 2 exactly

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Icon = styled.div`
  width: 20px;
  height: 20px;
  background: ${(props) => `linear-gradient(
    135deg,
    ${props.theme?.colors?.success || '#22C55E'} 0%,
    ${props.theme?.colors?.success || '#22C55E'}DD 100%
  )`};
  border-radius: 50%;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.primary
      : props.theme.colors.blueBlack};
`;

const Total = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${(props) => props.theme?.colors?.success || '#22C55E'};
`;

const CreditList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CreditItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.02)'
      : 'rgba(0,0,0,0.02)'};
  border-radius: 8px;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.08)'};
`;

const CreditInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CreditReason = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.primary
      : props.theme.colors.blueBlack};
  margin-bottom: 4px;
`;

const CreditDate = styled.div`
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748B'};
`;

const CreditAmount = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => props.theme?.colors?.success || '#22C55E'};
`;

const ViewMore = styled.div`
  text-align: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(0,0,0,0.06)'};
`;

const ViewMoreLink = styled.a`
  font-size: 16px;
  font-weight: 500;
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }

  &:after {
    content: '→';
    display: inline-block;
    margin-left: 2px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748B'};
  font-size: 16px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 24px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748B'};
  font-size: 16px;
`;

const OpenCreditsCard = ({ userId, routePrefix }) => {
  const appContext = useContext(AppContext);
  const accountId = appContext?.state?.accountId;

  // Determine if we're in admin context (no accountId means regular admin)
  const isRegularAdmin = !accountId;

  // Get total credits - use admin query if no accountId
  const {
    data: totalData,
    loading: totalLoading,
    error: totalError,
  } = useQuery(
    isRegularAdmin ? GetUserOpenCreditsTotalAdmin : GetUserOpenCreditsTotal,
    {
      variables: {
        input: isRegularAdmin
          ? {
              user_id: parseInt(userId),
            }
          : {
              user_id: parseInt(userId),
              account_id: parseInt(accountId),
            },
      },
      skip: !userId,
      onError: (error) => {
        console.error('Error fetching total credits:', error);
      },
    }
  );

  // Get credit list - use admin query if no accountId
  const {
    data: creditsData,
    loading: creditsLoading,
    error: creditsError,
  } = useQuery(isRegularAdmin ? GetUserCreditsAdmin : GetUserCredits, {
    variables: {
      input: isRegularAdmin
        ? {
            user_id: parseInt(userId),
            limit: 3,
          }
        : {
            user_id: parseInt(userId),
            account_id: parseInt(accountId),
            limit: 3,
          },
    },
    skip: !userId,
    onError: (error) => {
      console.error('Error fetching credits list:', error);
    },
  });

  const loading = totalLoading || creditsLoading;
  const totalAmount = isRegularAdmin
    ? totalData?.getUserOpenCreditsTotalAdmin || 0
    : totalData?.getUserOpenCreditsTotal || 0;

  // Both queries now return the same paginated structure
  const credits = isRegularAdmin
    ? creditsData?.getUserCreditsAdmin?.results || []
    : creditsData?.getUserOpenCreditsByAccount?.results || [];
  const totalCreditCount = isRegularAdmin
    ? creditsData?.getUserCreditsAdmin?.count || 0
    : creditsData?.getUserOpenCreditsByAccount?.count || 0;
  const hasMoreCredits = totalCreditCount > 3;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';

    // Handle MM-DD-YYYY format from API
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
      const [month, day, year] = dateStr.split('-');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }

    // Handle timestamp
    const date = new Date(parseInt(dateStr));
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatAmount = (amount) => {
    // Handle string amounts like "$25.00"
    if (typeof amount === 'string') {
      // If already formatted with $, return as is
      if (amount.startsWith('$')) return amount;
      // Otherwise parse and format
      const num = parseFloat(amount.replace(/[^0-9.-]/g, ''));
      if (isNaN(num)) return '$0.00';
      return `$${num.toFixed(2)}`;
    }
    // Handle numeric amounts
    const num = parseFloat(amount);
    if (isNaN(num)) return '$0.00';
    return `$${num.toFixed(2)}`;
  };

  // Show errors if any
  if (totalError || creditsError) {
    console.error('Query errors:', { totalError, creditsError });
    return (
      <Card style={{ marginBottom: '16px' }}>
        <EmptyState>Error loading credits</EmptyState>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card style={{ marginBottom: '16px' }}>
        <LoadingState>Loading credits...</LoadingState>
      </Card>
    );
  }

  // Don't render the component at all if there are no credits
  if (!credits.length || totalAmount === 0) {
    return null;
  }

  return (
    <Card style={{ marginBottom: '16px' }}>
      <Header>
        <TitleSection>
          <Icon />
          <Title>Open Credits</Title>
        </TitleSection>
        <Total>{formatAmount(totalAmount)}</Total>
      </Header>

      <CreditList>
        {credits.map((credit) => (
          <CreditItem key={credit.id}>
            <CreditInfo>
              <CreditReason>
                {credit.reason
                  ? credit.reason
                  : credit.transaction?.id
                  ? 'Applied to Purchase'
                  : 'Store Credit'}
              </CreditReason>
              <CreditDate>
                {isRegularAdmin &&
                  credit.account &&
                  credit.account.business_dba && (
                    <>{credit.account.business_dba} • </>
                  )}
                Issued {formatDate(credit.date)}
              </CreditDate>
            </CreditInfo>
            <CreditAmount>{formatAmount(credit.amount)}</CreditAmount>
          </CreditItem>
        ))}
      </CreditList>

      {hasMoreCredits && (
        <ViewMore>
          <ViewMoreLink
            as={Link}
            to={`${routePrefix}/openCredits/creditDetail/${userId}`}
          >
            View all {totalCreditCount} credits
          </ViewMoreLink>
        </ViewMore>
      )}
    </Card>
  );
};

OpenCreditsCard.propTypes = {
  userId: PropTypes.string.isRequired,
  routePrefix: PropTypes.string.isRequired,
};

export default OpenCreditsCard;
